import {QueryResult} from "@apollo/client";
import {Button, Dialog, InputGroup, Intent, Radio, RadioGroup, Switch, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import axios from "axios";
import {isValid} from "date-fns";
import {isArray} from "lodash";
import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Address} from "../../../../domain/geocode-types";
import {DriverLocation, SearchDriverLocationQuery, useSetTenantPrefMutation} from "../../../../generated/graphql";
import {createJsonPref, extractSimplePref, PreferenceContext} from "../../../../providers/PreferenceProvider";
import {useAuthState} from "../../../../providers/AuthProvider";
import {isCompleted, isPickupStop} from "../../../../utils/stops/StopUtils";
import {getTimeZoneIdByAddress} from "../../../../utils/General";
import {AppToaster} from "../../../../utils/toasters/toaster";
import {ActionButton, ConfirmationDialog} from "../../../common/ConfirmationDialog";
import {COLORS, Constants, TenantPreferences} from "../../../../constants/constants";
import {InformationIcon, WarningIcon} from "../../../common/icons";
import {getGPSStatusType, GPSStatusType} from "../../../map/driver/DriverMarkerV2";
import {TimeTag} from "../../../settings/ColorSettings/ColorizedIndicators/TriggerSelector";
import {
  DEFAULT_DWELL_MINUTES,
  MAX_DWELL_MINUTES,
  MIN_DWELL_MINUTES
} from "../../../settings/CompanySettings/common/constants";
import PlacesAutoCompleteInput from "../../../common/PlacesAutoCompleteInput";
import StopLocationSelector, {TStopLocation} from "./StopLocationSelector";
import {getFirstStopAfterPickupOptions, toStopLocation} from "./OptimizeDialog.service";
import TimePickerCommon from "./TimePickerCommon";
import {
  PRIMARY_BUTTON,
  PRIMARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR,
  distanceFromLeftOptimizeDialog
} from "./ManifestDialog.service";
import log from "loglevel";
import {findEarliestStopDateV2} from "../utils/ManifestDetails.utils";
import {getVehicleDwellTime} from "../../../../utils/VehicleUtils";
import {TVehicleConfiguration} from "../../../settings/VehicleConfiguration/VehicleConfiguration";
import {TManifestEnhanced, TManifestStopEnhanced} from "../../../common/types";

export interface OptimizeDialogProps {
  onClose: () => void;
  onSave: () => void;
  onDataChange: (data: OptimizeFormData) => void;
  homeAddress?: Address;
  manifest?: TManifestEnhanced;
  setIsOptimizeFromHomeLocation: React.Dispatch<React.SetStateAction<boolean>>;
  driverLocationResult?: QueryResult<SearchDriverLocationQuery>;
}

const convertToDateTimeLocalString = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export type OptimizeFormData = {
  ignoreTimeWindows?: boolean;
  startingLocation?: string;
  startingTime?: string;
  dwellTime?: number;
  endLocation?: string;
  /** DP-3187: set together, only while "First Stop After Pickup" is shown and a stop is picked */
  startingJobStopId?: number;
  firstStopAfterPickupJobStopId?: number;
};

export type TOptimizationInfoItem = {
  title: string;
  required: boolean;
  subTitle?: string[];
  infoIcon?: {
    icon: ReactElement<any, any>;
    content: string | JSX.Element | undefined;
  };
  /** Rendered after the title, e.g. the "Not relevant?" link */
  titleAction?: ReactElement;
};

type TOptimizationInfo = {
  startingLocation: TOptimizationInfoItem;
  firstStopAfterPickup: TOptimizationInfoItem;
  dataTimeTrip: TOptimizationInfoItem;
  dwellTime: TOptimizationInfoItem;
  finalLocation: TOptimizationInfoItem;
};

export const NOT_RELEVANT_MESSAGE = "If you are a Dispatch admin, you'll be able to remove this option for all users.";

enum LocationMessages {
  NotAvailable = "Driver's location isn't available",
  LatLngIssue = "Issues processing the lat/long of the most recent GPS point",
  OldGPS = "Driver's location data is at least 5m old and is unreliable",
  HomeNotAvailable = "Home Address Not Available"
}

const OptimizationsInfo: TOptimizationInfo = {
  startingLocation: {
    title: "Driver's Starting Location",
    required: true
  },
  firstStopAfterPickup: {
    title: "First Stop After Pickup",
    // optional, but the mockup shows no "(optional)" marker: the "Not relevant?" link takes that slot
    required: true,
    subTitle: ["The optimized route will begin at this stop immediately after the driver's first pickup."]
  },
  dataTimeTrip: {
    title: "Start date and time",
    required: true,
    subTitle: ["Timezone of starting time is based on the timezone of the Driver's starting location"]
  },
  dwellTime: {
    title: "Dwell Time Average",
    required: true,
    infoIcon: {
      icon: <InformationIcon scale={0.7} fill={"grey"} />,
      content: (
        <div>
          <p style={{margin: "0"}}>Minimum: {MIN_DWELL_MINUTES} minute</p>
          <p style={{margin: "0"}}>Maximum: {MAX_DWELL_MINUTES} minutes</p>
        </div>
      )
    }
  },
  finalLocation: {
    title: "Final Location",
    required: false
  }
};

const OptimizeDialog: React.FC<OptimizeDialogProps> = ({
  onClose,
  onSave,
  onDataChange,
  homeAddress,
  manifest,
  setIsOptimizeFromHomeLocation,
  driverLocationResult
}) => {
  const {tenantPreferences, tenantPrefsQueryRefetch} = useContext(PreferenceContext);
  const {isAdmin} = useAuthState();
  const [setTenantPref, {loading: isRemovingFirstStopOption}] = useSetTenantPrefMutation();
  const dwellTimeByVehicle = getVehicleDwellTime(
    manifest?.vehicleType.description,
    extractSimplePref(tenantPreferences, TenantPreferences.vehicleIconSetup).value as TVehicleConfiguration[]
  )?.toString();
  const initDwellTime =
    dwellTimeByVehicle ??
    Math.min(
      extractSimplePref(tenantPreferences, TenantPreferences.assumedOptimizationDwellTime, DEFAULT_DWELL_MINUTES)
        .value as number,
      MAX_DWELL_MINUTES
    ).toString();

  const isDispatcherIgnoreTimeWindows = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindows,
    true
  ).value as boolean;

  const isDispatcherIgnoreTimeWindowOnFirstTry = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
    true
  ).value as boolean;

  const showIgnoreTimeWindowsToggle = isDispatcherIgnoreTimeWindows && isDispatcherIgnoreTimeWindowOnFirstTry;
  const isDispatcherIgnoreTimeWindowAsDefault = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault,
    false
  ).value as boolean;

  const isDefineFirstStopAfterPickup = extractSimplePref(
    tenantPreferences,
    TenantPreferences.defineFirstStopAfterPickup,
    true
  ).value as boolean;

  const [isIgnoreTimeWindows, setIsIgnoreTimeWindows] = useState(
    showIgnoreTimeWindowsToggle && isDispatcherIgnoreTimeWindowAsDefault
  );
  const [startLocation, setStartLocation] = useState("");
  const [timeZoneAddress, setTimeZoneAddress] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState("");
  const [dwellTime, setDwellTime] = useState(initDwellTime);
  const [endLocation, setEndLocation] = useState("");
  const [earliestDate, setEarliestDate] = useState<Date | null>(null);
  const [currentDriverLocationPlace, setCurrentDriverLocationPlace] = useState("");
  const [currentDriverLocationIsOld, setCurrentDriverLocationIsOld] = useState(true);
  const [isOptimizeEnabled, setIsOptimizeEnabled] = useState(false);
  const [isStartDateTimeEdited, setIsStartDateTimeEdited] = useState(false);
  const [startStop, setStartStop] = useState<TManifestStopEnhanced | undefined>();
  const [firstStopAfterPickup, setFirstStopAfterPickup] = useState<TStopLocation | undefined>();
  const [isNotRelevantOpen, setIsNotRelevantOpen] = useState(false);

  // DP-3187: the section exists only for a driver starting at one of the manifest's pickups
  const showFirstStopAfterPickup = isDefineFirstStopAfterPickup && !!startStop && isPickupStop(startStop);
  // Ignore Time Windows and the start time change which pickups count as the start wave, so the options follow both
  const firstStopOptions = useMemo(
    () => (showFirstStopAfterPickup ? getFirstStopAfterPickupOptions(manifest?.stops, startStop?.jobStopId) : []),
    [showFirstStopAfterPickup, manifest?.stops, startStop?.jobStopId]
  );

  useEffect(() => {
    setFirstStopAfterPickup(undefined);
  }, [startStop?.jobStopId]);

  useEffect(() => {
    if (firstStopAfterPickup && !firstStopOptions.some((option) => option.key === firstStopAfterPickup.key)) {
      setFirstStopAfterPickup(undefined);
    }
  }, [firstStopOptions, firstStopAfterPickup]);

  useEffect(() => {
    if (startLocation) {
      getTimeZoneIdByAddress(startLocation)
        .then((res) => {
          if (res) {
            setTimeZoneAddress(res);
          } else {
            setTimeZoneAddress(undefined);
          }
        })
        .catch((error) => log.error(error));
    } else {
      setTimeZoneAddress(undefined);
    }
  }, [startLocation]);

  useEffect(() => {
    if (manifest && !isStartDateTimeEdited) {
      const findEarliestStopDate = findEarliestStopDateV2(manifest?.stops, {
        timezoneOfStartLocation: timeZoneAddress
      });
      setEarliestDate(findEarliestStopDate);
    }
  }, [isStartDateTimeEdited, manifest, timeZoneAddress]);

  useEffect(() => {
    const pinFirstStop = showFirstStopAfterPickup && !!firstStopAfterPickup;
    onDataChange({
      ignoreTimeWindows: isIgnoreTimeWindows,
      startingLocation: startLocation,
      dwellTime: Number(dwellTime),
      startingTime: startDate,
      endLocation: endLocation,
      startingJobStopId: pinFirstStop ? startStop?.jobStopId : undefined,
      firstStopAfterPickupJobStopId: pinFirstStop ? firstStopAfterPickup?.key : undefined
    });
  }, [
    startLocation,
    currentDriverLocationPlace,
    startDate,
    dwellTime,
    endLocation,
    onDataChange,
    isIgnoreTimeWindows,
    showFirstStopAfterPickup,
    firstStopAfterPickup,
    startStop?.jobStopId
  ]);

  useEffect(() => {
    if (!driverLocationResult?.loading) {
      const data = driverLocationResult?.data?.searchDriverLocations?.items as DriverLocation[];
      if (data && data.length > 0) {
        const driverLocation = data[0];
        const gpsStatusType = getGPSStatusType(driverLocation);
        setCurrentDriverLocationIsOld(gpsStatusType !== GPSStatusType.NEW);

        const lat = driverLocation.coordinates.split(",")[0];
        const lng = driverLocation.coordinates.split(",")[1];
        const response = axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=street_address&key=${Constants.GOOGLE_API_KEY}`
        );
        response.then((res) => {
          if (res.data.results.length > 0 && res.data.results[0].formatted_address) {
            setCurrentDriverLocationPlace(res.data.results[0].formatted_address);
          } else {
            setCurrentDriverLocationPlace(LocationMessages.LatLngIssue);
          }
        });
      } else {
        setCurrentDriverLocationPlace(LocationMessages.NotAvailable);
      }
    }
  }, [driverLocationResult?.data?.searchDriverLocations?.items, driverLocationResult?.loading]);

  const handleIgnoreTimeWindowsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsIgnoreTimeWindows(e.target.checked);
    },
    [setIsIgnoreTimeWindows]
  );

  const handleStartDateChange = useCallback((event: Date | null) => {
    if (!event) {
      setStartDate("");
    } else {
      setStartDate(convertToDateTimeLocalString(event));
    }
  }, []);

  const handleDwellTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDwellTime(event.target.value);
  };

  const closeNotRelevant = useCallback(() => setIsNotRelevantOpen(false), []);

  // Admin "Remove": turn the company setting off for every user, then let the pref refetch hide the section
  const handleRemoveFirstStopOption = useCallback(async () => {
    try {
      await setTenantPref({
        variables: {name: TenantPreferences.defineFirstStopAfterPickup, input: createJsonPref(false)}
      });
      tenantPrefsQueryRefetch?.();
      setFirstStopAfterPickup(undefined);
      setIsNotRelevantOpen(false);
    } catch (error) {
      log.error(error);
      AppToaster.show({
        intent: Intent.DANGER,
        message: "Couldn't remove the First Stop After Pickup option. Please try again."
      });
    }
  }, [setTenantPref, tenantPrefsQueryRefetch]);

  const handleCancel = () => {
    onClose();
    setEndLocation("");
    setStartLocation("");
    setStartDate("");
    setDwellTime("");
    setIsOptimizeEnabled(false);
    setTimeZoneAddress(undefined);
    setIsStartDateTimeEdited(false);
    setStartStop(undefined);
    setFirstStopAfterPickup(undefined);
    setIsNotRelevantOpen(false);
  };

  const handleOptimize = () => {
    onSave();
    handleCancel();
  };

  useEffect(() => {
    const isOptimizeEnable = Boolean(
      startLocation && isValid(new Date(startDate)) && dwellTime && Number(dwellTime) >= MIN_DWELL_MINUTES
    );
    setIsOptimizeEnabled(isOptimizeEnable);
  }, [startLocation, startDate, dwellTime]);

  useEffect(() => {
    return () => {
      handleCancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledDialog isOpen={true} onClose={onClose} distanceFromLeft={distanceFromLeftOptimizeDialog()}>
      <DialogContainer data-testid="optimization-dialog-container">
        <DialogHeader>
          <DialogTitle data-testid="dialog-title">MANIFEST OPTIMIZATION</DialogTitle>
          <DialogCloseIcon variant="minimal" data-testid="dialog-close-icon" onClick={onClose} icon="cross" />
        </DialogHeader>
        <DialogBody>
          {showIgnoreTimeWindowsToggle && (
            <Switch
              checked={isIgnoreTimeWindows}
              onChange={handleIgnoreTimeWindowsChange}
              label="Ignore Time Windows"
              style={{marginBottom: "0", fontSize: "16px", width: "fit-content"}}
              alignIndicator="right"
            />
          )}
          <OptimizationOptions
            optionInfo={OptimizationsInfo.startingLocation}
            renderer={
              <ProvideLocation
                homeAddress={homeAddress}
                currentAddress={currentDriverLocationPlace}
                type={ProvideLocationType.starting}
                onChange={setStartLocation}
                currentDriverLocationIsOld={currentDriverLocationIsOld}
                manifestStops={manifest?.stops ?? []}
                setIsOptimizeFromHomeLocation={setIsOptimizeFromHomeLocation}
                onSelectStop={setStartStop}
              />
            }
          />
          {showFirstStopAfterPickup && (
            <OptimizationOptions
              optionInfo={{
                ...OptimizationsInfo.firstStopAfterPickup,
                titleAction: (
                  <NotRelevantLink
                    type="button"
                    data-testid="first-stop-after-pickup-not-relevant"
                    onClick={() => setIsNotRelevantOpen(true)}
                  >
                    Not relevant?
                  </NotRelevantLink>
                )
              }}
              renderer={
                <StopLocationSelector
                  items={firstStopOptions}
                  selectedItem={firstStopAfterPickup}
                  onSelectItem={setFirstStopAfterPickup}
                  onClear={() => setFirstStopAfterPickup(undefined)}
                  testIdPrefix="first-stop-after-pickup"
                />
              }
            />
          )}
          <OptimizationOptions
            optionInfo={OptimizationsInfo.dataTimeTrip}
            renderer={
              <StartDateTime
                timeZoneAddress={timeZoneAddress}
                onChange={handleStartDateChange}
                earliestDate={earliestDate}
                setIsStartDateTimeEdited={setIsStartDateTimeEdited}
              />
            }
          />
          <OptimizationOptions
            optionInfo={OptimizationsInfo.dwellTime}
            renderer={<DwellTimeInput value={dwellTime} handleChangeDwell={handleDwellTimeChange} min={0} max={60} />}
          />
          <OptimizationOptions
            optionInfo={OptimizationsInfo.finalLocation}
            renderer={
              <ProvideLocation
                type={ProvideLocationType.final}
                startLocation={startLocation}
                homeAddress={homeAddress}
                onChange={setEndLocation}
              />
            }
          />
        </DialogBody>
        <Footer>
          <ActionButton data-testid="dialog-cancel-button" onClick={handleCancel}>
            Cancel
          </ActionButton>
          <ActionButton
            data-testid="dialog-optimize-button"
            disabled={!isOptimizeEnabled}
            onClick={handleOptimize}
            style={{
              color: "white",
              background: !isOptimizeEnabled ? "#d9d9d9" : "linear-gradient(180deg, #214A89 0%, #14305A 100%)",
              width: "92px"
            }}
            text="Optimize"
          />
        </Footer>
        <ConfirmationDialog
          testIdPrefix="first-stop-not-relevant"
          isVisible={isNotRelevantOpen}
          header="First Stop After Pickup"
          body={NOT_RELEVANT_MESSAGE}
          yesText={isAdmin ? "Remove" : "Okay"}
          yesTextColor={isAdmin ? undefined : "#137cbd"}
          noText="Okay"
          hideNoButton={!isAdmin}
          yesLoading={isRemovingFirstStopOption}
          handleYesClicked={isAdmin ? handleRemoveFirstStopOption : closeNotRelevant}
          handleNoClicked={closeNotRelevant}
          handleClose={closeNotRelevant}
        />
      </DialogContainer>
    </StyledDialog>
  );
};

export default OptimizeDialog;
interface DialogContainerProps {
  distanceFromLeft: number;
}
interface OptimizationOptionsProps {
  renderer: any;
  optionInfo: TOptimizationInfoItem;
}
export const OptimizationOptions = ({optionInfo, renderer}: OptimizationOptionsProps) => {
  const {title, required, subTitle, infoIcon, titleAction} = optionInfo;
  return (
    <OptimizationOptionsContainer
      data-testid={`optimize-option-container-${title.toLocaleLowerCase().replaceAll(" ", "-")}`}
      subTitle={subTitle}
    >
      <OptimizationTitle data-testid="optimize-option-title">
        {title}
        {infoIcon && (
          <Tooltip content={infoIcon.content} placement={"right"}>
            <OptimizationIconContainer>{infoIcon.icon}</OptimizationIconContainer>
          </Tooltip>
        )}
        {!required && <OptimizationRequired data-testid="optimize-option-optional">(optional)</OptimizationRequired>}
        {titleAction}
      </OptimizationTitle>
      {isArray(subTitle) && subTitle.length > 0 && (
        <>
          {subTitle?.map((x) => (
            <OptimizationSubTitle key={x} data-testid="optimize-option-subtitle">
              {x}
            </OptimizationSubTitle>
          ))}
        </>
      )}
      {renderer}
    </OptimizationOptionsContainer>
  );
};

export enum ProvideLocationType {
  starting,
  final
}
export interface ProvideLocationProps {
  type: ProvideLocationType;
  onChange: React.Dispatch<React.SetStateAction<string>>;
  currentAddress?: string;
  homeAddress?: Address;
  currentDriverLocationIsOld?: boolean;
  manifestStops?: TManifestStopEnhanced[];
  startLocation?: string;
  setIsOptimizeFromHomeLocation?: React.Dispatch<React.SetStateAction<boolean>>;
  /** DP-3187: the manifest stop behind "Stop Location from Manifest", undefined for any other choice */
  onSelectStop?: (stop?: TManifestStopEnhanced) => void;
}
type SelectTypeState = "home" | "current" | "custom" | "stop" | "start" | "";

export const ProvideLocation = ({
  type,
  onChange,
  currentAddress,
  homeAddress,
  currentDriverLocationIsOld,
  manifestStops,
  startLocation,
  setIsOptimizeFromHomeLocation,
  onSelectStop
}: ProvideLocationProps) => {
  const [selectTypeLocation, setSelectTypeLocation] = useState<SelectTypeState>("");
  const [isDisablePlaceAuto, setIsDisablePlaceAuto] = useState(true);
  const [placeInputPlace, setPlaceInputPlace] = useState("");
  const [selectedStopLocation, setSelectedStopLocation] = React.useState<TStopLocation | undefined>();

  const stopLocationOptions: TStopLocation[] | undefined = useMemo(
    () =>
      manifestStops
        ?.filter((stop) => !isCompleted(stop))
        .sort((a, b) => a.manifestSequence - b.manifestSequence)
        .map(toStopLocation) ?? [],
    [manifestStops]
  );

  const findManifestStop = useCallback(
    (item?: TStopLocation) => manifestStops?.find((stop) => stop.jobStopId === item?.key),
    [manifestStops]
  );

  const handleSelectPlaceInput = useCallback(
    (e: string) => {
      setPlaceInputPlace(e);
      onChange(e);
    },
    [setPlaceInputPlace, onChange]
  );

  const handleSelectStopLocation = useCallback(
    (item: TStopLocation) => {
      setSelectedStopLocation(item);
      onChange(item.address ?? "");
      onSelectStop?.(findManifestStop(item));
    },
    [onChange, onSelectStop, findManifestStop]
  );

  const handleSelectType = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const typeOfLocation = e.currentTarget.value as SelectTypeState;
      setSelectTypeLocation(typeOfLocation);
      onSelectStop?.(typeOfLocation === "stop" ? findManifestStop(selectedStopLocation) : undefined);
      if (typeOfLocation === "home") {
        setPlaceInputPlace(homeAddress?.formatAddress() ?? "");
        setIsDisablePlaceAuto(true);
        onChange(homeAddress?.formatAddress() ?? "");
        setIsOptimizeFromHomeLocation?.(true);
      }
      if (typeOfLocation === "current") {
        setPlaceInputPlace(currentAddress ?? "");
        setIsDisablePlaceAuto(true);
        onChange(currentAddress ?? "");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "stop") {
        setPlaceInputPlace("");
        setIsDisablePlaceAuto(true);
        onChange(selectedStopLocation?.address ?? "");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "custom") {
        setPlaceInputPlace("");
        setIsDisablePlaceAuto(false);
        onChange("");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "start") {
        setPlaceInputPlace(startLocation ?? "");
        setIsDisablePlaceAuto(true);
        onChange(startLocation ?? "");
      }
    },
    [
      homeAddress,
      onChange,
      setIsOptimizeFromHomeLocation,
      currentAddress,
      selectedStopLocation,
      startLocation,
      onSelectStop,
      findManifestStop
    ]
  );

  const clearSelection = useCallback(() => {
    setSelectTypeLocation("");
    setPlaceInputPlace("");
    onChange("");
    onSelectStop?.(undefined);
    setIsDisablePlaceAuto(true);
  }, [onChange, onSelectStop]);

  const handleClickRadio = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      if (
        type === ProvideLocationType.final &&
        event.currentTarget.value === selectTypeLocation &&
        selectTypeLocation !== ""
      ) {
        clearSelection();
      }
    },
    [clearSelection, selectTypeLocation, type]
  );

  const renderTooltipContent = useCallback(() => {
    if (!currentAddress) return LocationMessages.NotAvailable;
    if ([LocationMessages.LatLngIssue, LocationMessages.NotAvailable].includes(currentAddress as any))
      return currentAddress;
    if (currentDriverLocationIsOld) {
      return LocationMessages.OldGPS;
    }
  }, [currentAddress, currentDriverLocationIsOld]);

  const isDisableCurrentLocationOption = useMemo(() => {
    return (
      !currentAddress ||
      [LocationMessages.LatLngIssue, LocationMessages.NotAvailable].includes(currentAddress as any) ||
      currentDriverLocationIsOld
    );
  }, [currentAddress, currentDriverLocationIsOld]);

  useEffect(() => {
    if (selectTypeLocation === "current" && currentDriverLocationIsOld) {
      clearSelection();
    }
  }, [clearSelection, currentDriverLocationIsOld, selectTypeLocation]);

  useEffect(() => {
    if (selectTypeLocation === "start") {
      if (!startLocation) {
        clearSelection();
      } else {
        setPlaceInputPlace(startLocation);
        onChange(startLocation);
      }
    }
  }, [clearSelection, onChange, selectTypeLocation, startLocation]);

  return (
    <RadioGroupCustom onChange={handleSelectType} selectedValue={selectTypeLocation}>
      <Radio value={"home"} onClick={handleClickRadio} disabled={!homeAddress}>
        <DialogText primary={Boolean(homeAddress)}>Home</DialogText>{" "}
        <DialogText className="optimize-subtext">
          {`( ${homeAddress?.formatAddress() ?? LocationMessages.HomeNotAvailable} )`}
        </DialogText>
      </Radio>
      {type === ProvideLocationType.starting && (
        <Radio disabled={isDisableCurrentLocationOption} value={"current"}>
          <DialogText primary={!isDisableCurrentLocationOption}>Current Location</DialogText>
          {isDisableCurrentLocationOption && (
            <Tooltip
              className="custom-tooltip"
              content={renderTooltipContent()}
              placement="left"
              transitionDuration={0}
            >
              <WarningIcon />
            </Tooltip>
          )}
        </Radio>
      )}
      {type === ProvideLocationType.starting && (
        <Radio value={"stop"}>
          <DialogText primary>Stop Location from Manifest</DialogText>
          {selectTypeLocation === "stop" && (
            <StopLocationSelector
              items={stopLocationOptions}
              selectedItem={selectedStopLocation}
              onSelectItem={handleSelectStopLocation}
            />
          )}
        </Radio>
      )}
      {type === ProvideLocationType.final && (
        <Radio onClick={handleClickRadio} value={"start"} disabled={!startLocation}>
          <DialogText primary={Boolean(startLocation)}>Start Location</DialogText>{" "}
          <DialogText className="optimize-subtext">{startLocation ? `( ${startLocation} )` : ""}</DialogText>
        </Radio>
      )}
      <RadioCustomContainer onChange={handleSelectType} selectedValue={selectTypeLocation}>
        <Radio onClick={handleClickRadio} value={"custom"} className="custom-radio">
          <DialogText primary>Custom</DialogText>
        </Radio>
        {selectTypeLocation && selectTypeLocation !== "stop" && (
          <PlacesAutoCompleteInput
            value={placeInputPlace}
            onChange={handleSelectPlaceInput}
            disabled={isDisablePlaceAuto}
          />
        )}
      </RadioCustomContainer>
    </RadioGroupCustom>
  );
};
export interface StartDateTimeProps {
  onChange: (...arg: any) => void;
  earliestDate: Date | null;
  timeZoneAddress: string | undefined;
  setIsStartDateTimeEdited: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StartDateTime = ({
  onChange,
  earliestDate,
  timeZoneAddress,
  setIsStartDateTimeEdited
}: StartDateTimeProps) => {
  return (
    <TimePickerCommon
      onChange={onChange}
      earliestDate={earliestDate}
      timeZoneAddress={timeZoneAddress}
      setIsStartDateTimeEdited={setIsStartDateTimeEdited}
    />
  );
};
export interface DwellTimePickerProps {
  value: string;
  min: number;
  max: number;
  handleChangeDwell: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DwellTimeInput = ({value, handleChangeDwell, min, max}: DwellTimePickerProps) => {
  const [openWarningToolTip, setOpenWarningToolTip] = useState(false);

  const onChangeDwell = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (Number(e.target.value) < min || Number(e.target.value) > max || isNaN(Number(e.target.value))) return;
      if (Number(e.target.value) < MIN_DWELL_MINUTES && e.target.value !== "") {
        setOpenWarningToolTip(true);
      } else {
        setOpenWarningToolTip(false);
      }
      handleChangeDwell(e);
    },
    [handleChangeDwell, min, max]
  );
  return (
    <DwellTimeContainer>
      <Tooltip
        isOpen={openWarningToolTip}
        intent="danger"
        content={`Dwell time must be at least ${MIN_DWELL_MINUTES} minute`}
        placement="top"
      >
        <DwellInput
          data-testid="dwell-input"
          type="number"
          placeholder="5"
          value={value}
          onChange={onChangeDwell}
          rightElement={<TimeTag>min</TimeTag>}
        />
      </Tooltip>
    </DwellTimeContainer>
  );
};

const DwellInput = styled(InputGroup)`
  width: fit-content;

  .bp5-input {
    width: 184px;
    height: 32px;
    border-radius: 4px;
    border: 1px solid #c3c3c36e;
    box-shadow: none;
    padding: 0 12px;

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    ::placeholder {
      color: ${SECONDARY_TEXT_COLOR};
    }
  }

  .bp5-tag {
    margin-right: 12px;
  }
`;

const DwellTimeContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const StyledDialog = styled(Dialog)<DialogContainerProps>`
  position: absolute;
  left: ${(props) => props.distanceFromLeft && `${props.distanceFromLeft}px`};
  width: 485px;
  &.bp5-dialog {
    background-color: #fcfcfc;
    border-radius: 0;
    border-radius: 16px;
    font-family: Roboto, sans-serif;
  }
`;

export const DialogContainer = styled.div`
  padding: 24px;
`;
export const DialogHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
export const DialogBody = styled.div<{gap?: number}>`
  padding: 24px 8px 32px 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap ?? 32}px;
  justify-content: space-between;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: right;
  gap: 10px;
`;

export const DialogTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #797979;
  line-height: 1;
  margin: 0;
`;
export const DialogCloseIcon = styled(Button)``;

interface OptimizationOptionsContainerProps {
  subTitle?: string[];
}
const OptimizationOptionsContainer = styled.div<OptimizationOptionsContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const OptimizationTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${PRIMARY_TEXT_COLOR};

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const OptimizationIconContainer = styled.div`
  transform: translateY(2px);
`;

const OptimizationSubTitle = styled.div`
  padding: 0;
  color: ${SECONDARY_TEXT_COLOR};
  font-size: 12px;
`;
const OptimizationRequired = styled.span`
  color: ${SECONDARY_TEXT_COLOR};
`;

const NotRelevantLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
  font-size: 12px;
  color: ${COLORS.BLUE};
`;

const RadioGroupCustom = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .bp5-control input:checked ~ .bp5-control-indicator {
    background-color: ${PRIMARY_BUTTON} !important;
  }
  .bp5-control input:checked ~ span {
    color: ${PRIMARY_TEXT_COLOR};
  }
  .custom-tooltip {
    position: absolute;
    right: 0;
    bottom: 0;
  }

  .bp5-radio {
    margin: 0;
  }
`;
const RadioCustomContainer = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  height: fit-content;
`;

export const DialogText = styled.span<{primary?: boolean}>`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.primary ? `${PRIMARY_TEXT_COLOR}` : `${SECONDARY_TEXT_COLOR}`)};
  &.optimize-subtext {
    color: ${SECONDARY_TEXT_COLOR} !important;
  }
  .bp5-button {
    padding: 0 5px 0 5px;
    vertical-align: baseline;
  }
`;
